import { IonButton, IonButtons, IonContent, IonDatetime, IonDatetimeButton, IonHeader, IonIcon, IonItem, IonLabel, IonModal, IonPage, IonSelect, IonSelectOption, IonTitle, IonToolbar, useIonViewDidEnter } from '@ionic/react';
import dayjs from 'dayjs';
import { arrowBack, calendar } from 'ionicons/icons';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import React, { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useHistory } from 'react-router-dom';
import { Buffer } from 'buffer';
import { urlBase } from '../../../../../Utils/Services';
import './OperacoesTable.css';

function formatCnpj(cnpj: string) {
    if (!cnpj) {
        return '';
    }

    const cleaned = cnpj.replace(/\D/g, '');
    if (cleaned.length !== 14) {
        return cnpj;
    }

    return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

function getNumeroNota(nota: any) {
    if (nota?.numeroNotaFiscal) {
        return nota.numeroNotaFiscal;
    }

    if (nota?.notaFiscal && nota.notaFiscal.length >= 34) {
        return nota.notaFiscal.substr(25, 9);
    }

    return '';
}

function pickDevolucaoFromOperacao(operacao: any, nota: any) {
    const lista = operacao?.notasDevolvidas || operacao?.notasDevolucao || operacao?.devolucaoNotas || [];
    const numeroNota = getNumeroNota(nota);

    if (!Array.isArray(lista)) {
        return {};
    }

    return lista.find((item: any) => {
        const nf = item?.notaFiscal || item?.numeroNotaFiscal || item?.nf || '';
        return nf === nota?.notaFiscal || nf === numeroNota;
    }) || {};
}

function DevolucaoTable(props: { rows: any[] }) {
    if (!props.rows || props.rows.length <= 1) {
        return <div>Nao ha dados para a data pesquisada</div>;
    }

    return (
        <table className="center">
            <thead>
                <tr>
                    {props.rows[0].map((col: string, index: number) => (
                        <th key={`head-${index}`}>{col}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {props.rows.slice(1).map((row: any[], rowIndex: number) => (
                    <tr key={`row-${rowIndex}`}>
                        {row.map((col: string, colIndex: number) => (
                            <td key={`col-${rowIndex}-${colIndex}`}>{col}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const RelatorioDevolucao: React.FC = () => {
    const history = useHistory();
    const datetime = useRef<null | HTMLIonDatetimeElement>(null);

    const [stateLoja, setStateLoja] = useState<any>();
    const [stateLojaNome, setStateLojaNome] = useState<any>();
    const [stateData, setStateData] = useState<any>(new Date().toISOString());
    const [statePeriodo, setStatePeriodo] = useState<any>('diario');
    const [stateCarregado, setStateCarregado] = useState<boolean>(false);
    const [stateRows, setStateRows] = useState<any[]>([]);
    const [stateRowsExport, setStateRowsExport] = useState<any[]>([]);

    useIonViewDidEnter(() => {
        document.getElementsByTagName('ion-tab-bar')[0].style.display = 'none';
    });

    const funcConfirm = () => {
        datetime.current?.confirm(true);
    };

    async function carregarOperacoesSegmentada(lojasParam: any, data: string) {
        let token: string = '';
        const tkn = localStorage.getItem('token');
        if (typeof tkn === 'string') {
            token = tkn;
        }

        const dataFormatada = dayjs(data).format('YYYY-MM-DD');
        const urlFinal = `${urlBase.url}/operacoes?filtro=Concluido&lojas=${Buffer.from(JSON.stringify(lojasParam)).toString('base64')}&data=${dataFormatada}&periodo=${statePeriodo}`;

        const resp: Response = await fetch(urlFinal, {
            method: urlBase.method,
            headers: [['token', token]],
            credentials: 'include',
        });

        if (resp.status === 400 || resp.status === 401) {
            history.replace('/login/');
            return [];
        }

        const res = await resp.json();
        return res?.Items || [];
    }

    async function carregarDados() {
        const operacoes = await carregarOperacoesSegmentada(stateLojaNome, stateData);

        const header = ['Data', 'Fornecedor', 'CNPJ', 'NF', 'Valor', 'Venc.', 'Motivo', 'Responsável'];
        const rows = [header];

        operacoes.forEach((operacao: any) => {
            const notas = operacao?.notasAdicionais?.length ? operacao.notasAdicionais : [{}];

            notas.forEach((nota: any) => {
                const devolucao = pickDevolucaoFromOperacao(operacao, nota);

                if (Object.keys(devolucao).length > 0) {
                    const data = dayjs(operacao?.dataEncerramento || operacao?.dataInicio).format('DD/MM/YY');
                    const fornecedor = nota?.razaoSocial || operacao?.fornecedor || 'Nao informado';
                    const cnpj = formatCnpj(nota?.cnpj || operacao?.cnpj || '');
                    const nf = getNumeroNota(nota) || 'Nao informado';
                    const valor = devolucao?.valor || nota?.valor || nota?.valorNota || '';
                    const vencimento = devolucao?.vencimento || nota?.vencimento || '';
                    const motivo = devolucao?.motivo || nota?.motivoDevolucao || operacao?.motivoDevolucao || '';
                    //const cliente = operacao?.cliente || operacao?.clienteNome || stateLoja || '';
                    const responsavel = operacao?.responsavelDevolucao || '';

                    rows.push([
                        data,
                        fornecedor,
                        cnpj,
                        nf,
                        valor?.toString?.() || '',
                        vencimento?.toString?.() || '',
                        motivo?.toString?.() || '',
                        responsavel?.toString?.() || '',
                    ]);
                }
            });
        });

        setStateRows(rows);
        setStateRowsExport(rows);
    }

    function gerarRelatorioPDF(rows: any[]) {
        (pdfMake as any).vfs = (pdfFonts as any).vfs;

        const reportDetails = [
            { text: 'Devolucao de Nota Fiscal', style: 'title' },
            { text: stateLoja || '', style: 'subheader' },
            {
                text: statePeriodo === 'diario'
                    ? `Data: ${dayjs(stateData).format('DD/MM/YYYY')}`
                    : `Mes: ${dayjs(stateData).format('MM/YYYY')}`,
                style: 'subheader',
            },
            {
                table: {
                    headerRows: 1,
                    widths: [48, 110, 74, 46, 55, 45, '*', 65],
                    body: rows,
                },
                layout: {
                    fillColor: (rowIndex: number) => (rowIndex === 0 ? '#ECECEC' : null),
                },
                style: 'tableText',
            },
        ];

        const docDefinitions: any = {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [16, 20, 16, 20],
            content: reportDetails,
            styles: {
                title: {
                    fontSize: 18,
                    bold: true,
                    alignment: 'center',
                    margin: [0, 0, 0, 10],
                },
                subheader: {
                    fontSize: 10,
                    bold: true,
                    margin: [0, 2, 0, 4],
                },
                tableText: {
                    fontSize: 8,
                },
            },
        };

        pdfMake.createPdf(docDefinitions).download('relatorio-cancelamentos.pdf');
    }

    return (
        <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonButton onClick={() => history.goBack()} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonIcon slot="icon-only" icon={arrowBack} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Relatorio de Devoluções</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent color="light" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <div style={{ textAlign: 'center' }} hidden={stateCarregado}>
                    <IonItem hidden={false} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Periodo</IonLabel>
                        <IonSelect value={statePeriodo} onIonChange={(e) => setStatePeriodo(e.detail.value)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonSelectOption value="diario" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Diario</IonSelectOption>
                            <IonSelectOption value="mensal" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Mensal</IonSelectOption>
                        </IonSelect>
                    </IonItem>

                    <IonItem hidden={statePeriodo !== 'diario'} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Data</IonLabel>
                        <IonDatetimeButton slot="end" datetime="datetime-devolucao" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonDatetimeButton>
                        <IonIcon slot="end" icon={calendar} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonIcon>
                    </IonItem>

                    <IonItem hidden={statePeriodo === 'diario'} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Mes</IonLabel>
                        <IonDatetimeButton slot="end" datetime="datetimemonth-devolucao" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonDatetimeButton>
                        <IonIcon slot="end" icon={calendar} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonIcon>
                    </IonItem>

                    <IonItem hidden={(window.localStorage.getItem('lojas') && JSON.parse(window.localStorage.getItem('lojas') || '').length > 0) ? false : true} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Loja</IonLabel>
                        <IonSelect placeholder="Selecione a loja" onIonChange={(e) => { setStateLoja(e.detail.value.split('|')[1]); setStateLojaNome(e.detail.value.split('|')[0]); }} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {JSON.parse(window.localStorage.getItem('lojas') || '[]').map((valor: any) => (
                                <IonSelectOption key={valor.clienteLojaId} value={`${valor.clienteLojaId}|${valor.clienteLojaNome}`} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{valor.clienteLojaNome}</IonSelectOption>
                            ))}
                        </IonSelect>
                    </IonItem>

                    <p></p>
                    <IonButton
                        onClick={async () => {
                            if (stateLoja === undefined) {
                                return;
                            }

                            setStateCarregado(true);
                            await carregarDados();
                        }}
                        expand="block"
                        type="submit"
                        fill="solid"
                        color="tertiary"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        Buscar
                    </IonButton>
                </div>

                <div style={{ textAlign: 'center' }} hidden={!stateCarregado}>
                    <IonItem lines="none" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel style={{ textAlign: 'center' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{stateLoja}</IonLabel>
                    </IonItem>
                    <IonItem lines="none" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel hidden={statePeriodo !== 'diario'} style={{ textAlign: 'center' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Data: {dayjs(stateData).format('DD/MM/YYYY')}</IonLabel>
                        <IonLabel hidden={statePeriodo !== 'mensal'} style={{ textAlign: 'center' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Mes: {dayjs(stateData).format('MM/YYYY')}</IonLabel>
                    </IonItem>

                    <p></p>
                    <IonButton onClick={() => gerarRelatorioPDF(stateRowsExport)} type="button" fill="solid" color="tertiary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Gerar PDF</IonButton>
                    <CSVLink data={stateRowsExport} separator=";"><IonButton type="button" fill="solid" color="tertiary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Gerar CSV</IonButton></CSVLink>
                    <IonButton onClick={() => { setStateRows([]); setStateRowsExport([]); setStateCarregado(false); }} type="button" fill="solid" color="tertiary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Limpar</IonButton>
                    <p></p>
                    <DevolucaoTable rows={stateRows}></DevolucaoTable>
                </div>

                <p></p>

                <IonModal keepContentsMounted={true} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonDatetime ref={datetime} id="datetime-devolucao" presentation="date" value={stateData} onIonChange={(e) => { setStateData(e.detail.value); funcConfirm(); }} min="2022-03-01" max={dayjs(new Date()).toISOString()} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonDatetime>
                </IonModal>

                <IonModal keepContentsMounted={true} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonDatetime showDefaultButtons={true} id="datetimemonth-devolucao" presentation="month-year" value={stateData} onIonChange={(e) => setStateData(e.detail.value)} min="2022-03-01" max={dayjs(new Date()).toISOString()} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonDatetime>
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default RelatorioDevolucao;
